import React, { useContext, useRef, useState } from 'react';

import { FeedbackType } from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import thumbsDownTokens from '@amzn/meridian-tokens/base/icon/thumbs-down';
import thumbsUpTokens from '@amzn/meridian-tokens/base/icon/thumbs-up';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Popover from '@amzn/meridian/popover';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Textarea from '@amzn/meridian/textarea';

import { AppContext } from 'src/contexts/appContext';

import { IntermediateMessagesCollapse } from '../IntermediateMessagesCollapse';
import { MessageItem } from '../MessageItem';
import { MessageInteraction } from '../utils';
import { submitFeedback } from '../utils/api';

interface MessageGroupProps {
    interaction: MessageInteraction;
    conversationId?: string;
    showTechnicalMessages?: boolean;
}

export const MessageGroup: React.FC<MessageGroupProps> = ({ interaction, conversationId, showTechnicalMessages }) => {
    const { userInfo, setTopAlertMsg } = useContext(AppContext);
    const thumbsUpButtonRef = useRef<HTMLButtonElement>(null);
    const thumbsDownButtonRef = useRef<HTMLButtonElement>(null);

    const [feedbackState, setFeedbackState] = useState<{
        isSubmitted: boolean;
        submittedType?: FeedbackType;
        isSubmitting: boolean;
        activePopover?: FeedbackType;
    }>({
        isSubmitted: false,
        isSubmitting: false
    });

    const handleFeedbackClick = (type: FeedbackType) => {
        if (feedbackState.isSubmitted || feedbackState.isSubmitting) return;

        setFeedbackState((prev) => ({
            ...prev,
            activePopover: type
        }));
    };

    const handleFeedbackSubmit = async (feedbackText: string) => {
        if (!conversationId || !interaction.finalAnswer) return;

        setFeedbackState((prev) => ({ ...prev, isSubmitting: true }));

        try {
            const success = await submitFeedback(
                conversationId,
                userInfo.username,
                interaction.finalAnswer!.timestamp,
                feedbackState.activePopover!,
                feedbackText || undefined,
                interaction.userMessage.messageContent ?? '',
                interaction.finalAnswer!.messageContent ?? ''
            );

            if (success) {
                setFeedbackState({
                    isSubmitted: true,
                    submittedType: feedbackState.activePopover,
                    isSubmitting: false,
                    activePopover: undefined
                });
            } else {
                throw new Error('Feedback submission failed');
            }
        } catch (error) {
            console.error('Error submitting feedback:', error);
            setTopAlertMsg(error);
            setFeedbackState((prev) => ({
                ...prev,
                isSubmitting: false,
                activePopover: undefined
            }));
        }
    };

    const handlePopoverClose = () => {
        if (feedbackState.isSubmitting) return; // Prevent closing while submitting

        setFeedbackState((prev) => ({
            ...prev,
            activePopover: undefined
        }));
    };

    const getButtonType = (buttonType: FeedbackType): 'link' | 'primary' => {
        if (feedbackState.isSubmitted && feedbackState.submittedType === buttonType) {
            return 'primary';
        }
        if (feedbackState.activePopover === buttonType) {
            return 'primary';
        }
        return 'link';
    };

    const isButtonDisabled = feedbackState.isSubmitted || feedbackState.isSubmitting;

    return (
        <Column spacing="400">
            {/* User message */}
            <MessageItem message={interaction.userMessage} conversationId={conversationId} />

            {/* Intermediate messages (collapsed if more than 5) */}
            {interaction.intermediateMessages.length > 0 && (
                <IntermediateMessagesCollapse
                    messages={interaction.intermediateMessages}
                    conversationId={conversationId}
                    showTechnicalMessages={showTechnicalMessages}
                />
            )}

            {/* Final answer (if exists) */}
            {interaction.finalAnswer && (
                <Column spacing="100">
                    <MessageItem message={interaction.finalAnswer} conversationId={conversationId} />
                    <Row maxWidth="60%" alignmentHorizontal="justify">
                        <Text type="b200" color="secondary">
                            <em>This response was generated by AI. Please review and validate for accuracy.</em>
                        </Text>
                        <Row spacing="100">
                            <Button
                                ref={thumbsUpButtonRef}
                                type={getButtonType(FeedbackType.GOOD)}
                                size="small"
                                onClick={() => handleFeedbackClick(FeedbackType.GOOD)}
                                disabled={isButtonDisabled}
                            >
                                <Icon tokens={thumbsUpTokens} />
                            </Button>
                            <Button
                                ref={thumbsDownButtonRef}
                                type={getButtonType(FeedbackType.BAD)}
                                size="small"
                                onClick={() => handleFeedbackClick(FeedbackType.BAD)}
                                disabled={isButtonDisabled}
                            >
                                <Icon tokens={thumbsDownTokens} />
                            </Button>

                            {thumbsUpButtonRef.current && (
                                <FeedbackPopover
                                    anchorRef={thumbsUpButtonRef}
                                    isOpen={feedbackState.activePopover === FeedbackType.GOOD}
                                    onClose={handlePopoverClose}
                                    onSubmit={handleFeedbackSubmit}
                                    feedbackType={FeedbackType.GOOD}
                                    isSubmitting={feedbackState.isSubmitting}
                                />
                            )}

                            {thumbsDownButtonRef.current && (
                                <FeedbackPopover
                                    anchorRef={thumbsDownButtonRef}
                                    isOpen={feedbackState.activePopover === FeedbackType.BAD}
                                    onClose={handlePopoverClose}
                                    onSubmit={handleFeedbackSubmit}
                                    feedbackType={FeedbackType.BAD}
                                    isSubmitting={feedbackState.isSubmitting}
                                />
                            )}
                        </Row>
                    </Row>
                </Column>
            )}
        </Column>
    );
};

interface FeedbackPopoverProps {
    anchorRef: React.RefObject<HTMLButtonElement>;
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (feedbackText: string) => Promise<void>;
    feedbackType: FeedbackType;
    isSubmitting: boolean;
}

const FeedbackPopover: React.FC<FeedbackPopoverProps> = ({
    anchorRef,
    isOpen,
    onClose,
    onSubmit,
    feedbackType,
    isSubmitting
}) => {
    const [feedbackText, setFeedbackText] = useState('');

    const handleSubmit = async () => {
        await onSubmit(feedbackText);
        setFeedbackText(''); // Clear text after submission
    };

    const handleCancel = () => {
        setFeedbackText(''); // Clear text on cancel
        onClose();
    };

    return (
        <Popover
            anchorNode={anchorRef.current!}
            open={isOpen}
            position="top"
            onClose={onClose}
            minWidth="30%"
            maxWidth="60%"
        >
            <Column alignmentHorizontal="start" spacingInset="300">
                <Text type="b200">
                    {feedbackType === FeedbackType.GOOD ? 'Positive Feedback' : 'Negative Feedback'}
                </Text>
                <Textarea
                    value={feedbackText}
                    onChange={setFeedbackText}
                    placeholder="Provide additional feedback"
                    width="100%"
                    rows={1}
                />
                <Row spacing="200">
                    <Button type="primary" size="small" onClick={handleSubmit} disabled={isSubmitting}>
                        {isSubmitting ? 'Submitting...' : 'Submit'}
                    </Button>
                    <Button type="secondary" size="small" onClick={handleCancel} disabled={isSubmitting}>
                        Cancel
                    </Button>
                </Row>
            </Column>
        </Popover>
    );
};
